/**
 * The Support inbox's write client: the console's Support inbox write exception
 * (owner-approved 2026-10-03). The Worker side is
 * `src/worker/support-inbox-writes.ts`.
 *
 * Same-origin `fetch` with `credentials: "same-origin"` (the operator's
 * HttpOnly session cookie is the credential, never a bearer), the custom
 * `X-Admin-Support-Inbox-Write: 1` header the Worker demands, and a JSON body.
 * The actor is never sent: the Worker takes it from the session, and the only
 * assignee the browser can name is "me".
 */
import type {
  MailSyncStatus,
  QuoteDetail,
  SendIdentity,
  TicketDetail,
} from "@/types-support";

export interface InboxWriteFailure extends Error {
  status: number;
  code: string | null;
}

async function post<T>(path: string, body: object): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Support-Inbox-Write": "1",
    },
    body: JSON.stringify(body),
  });
  const result = (await response.json().catch(() => null)) as {
    data?: T;
    error?: { code?: string; message?: string };
  } | null;
  if (!response.ok) {
    throw Object.assign(
      new Error(
        result?.error?.message ?? `Request failed (${response.status}).`,
      ),
      { status: response.status, code: result?.error?.code ?? null },
    ) as InboxWriteFailure;
  }
  return result?.data as T;
}

/** The same write for the chat client (`lib/support-chat.ts`), which rides this gate. */
export const postSupportInboxWrite = post;

const BASE = "/api/admin/support/inbox";

function ticketPath(id: string, action: string): string {
  return `${BASE}/${encodeURIComponent(id)}/${action}`;
}

export interface ReplyDraft {
  body: string;
  sentAs: SendIdentity;
  cc: string[];
  setStatus: "waiting" | "solved";
  /** One per draft: the same key twice sends once. */
  idempotencyKey: string;
  /** A chat ticket answered by email instead of in the chat. */
  mode?: "email";
  /** The first email of a contact-form ticket or a chat (`replySubjectEditable`). */
  subject?: string;
  /** The macro the text started from (Insert macro), checked by nt-support. */
  macroId?: string;
  macroVersion?: number;
}

export const NOT_EMAILED_MESSAGE =
  "The support service posted this reply into the chat instead of emailing it, so the customer got no email. Don't send it again: the text is already in the chat. Deploy nt-support, then email the chat.";

// nt-support's chat text rule (apps/support/src/chat/text.ts chatBodyOf):
// CRLF folded, bidi and control characters dropped, trimmed.
const BIDI_CONTROLS = /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;
const OTHER_CONTROLS =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200B\u2060\uFEFF]/g;

function chatText(body: string): string {
  return body
    .replace(/\r\n?/g, "\n")
    .replace(BIDI_CONTROLS, "")
    .replace(OTHER_CONTROLS, "")
    .trim();
}

/**
 * Whether a `mode: "email"` reply was emailed. Only a chat can be answered in
 * two ways, so email and form tickets always were. On a chat, an nt-support
 * older than the chat's email mode ignores `mode` and posts the reply into the
 * chat: the newest staff row with this text then came by chat. No such row
 * (the D1 projection lagging behind the chat) is not proof of that, so it
 * counts as emailed.
 */
export function showsEmailedReply(detail: TicketDetail, body: string): boolean {
  if (detail?.channel !== "chat") return true;
  const text = chatText(body);
  const newest = [...(detail.messages ?? [])]
    .reverse()
    .find(
      (message) =>
        message.direction === "outbound" &&
        message.chat?.authorKind === "staff" &&
        chatText(message.bodyText ?? "") === text,
    );
  return newest === undefined || newest.chat?.via === "email";
}

export async function replyToTicket(
  id: string,
  draft: ReplyDraft,
): Promise<TicketDetail> {
  const detail = await post<TicketDetail>(ticketPath(id, "reply"), {
    body: draft.body,
    sentAs: draft.sentAs,
    ...(draft.cc.length > 0 ? { cc: draft.cc } : {}),
    setStatus: draft.setStatus,
    idempotencyKey: draft.idempotencyKey,
    ...(draft.mode !== undefined ? { mode: draft.mode } : {}),
    ...(draft.subject !== undefined ? { subject: draft.subject } : {}),
    ...(draft.macroId !== undefined && draft.macroVersion !== undefined
      ? { macroId: draft.macroId, macroVersion: draft.macroVersion }
      : {}),
  });
  if (draft.mode === "email" && !showsEmailedReply(detail, draft.body)) {
    throw Object.assign(new Error(NOT_EMAILED_MESSAGE), {
      status: 502,
      code: "NOT_EMAILED",
    }) as InboxWriteFailure;
  }
  return detail;
}

export function addTicketNote(id: string, body: string): Promise<TicketDetail> {
  return post(ticketPath(id, "note"), { body });
}

export interface TicketUpdate {
  status?: "open" | "waiting" | "solved";
  /** The session admin, or nobody. */
  assignee?: "me" | null;
  /** Replaces the whole set. */
  tags?: string[];
  /** Merged into the ticket's current tags by nt-support in one write; not with `tags`. */
  addTags?: string[];
  removeTags?: string[];
}

export function updateTicket(
  id: string,
  update: TicketUpdate,
): Promise<TicketDetail> {
  return post(ticketPath(id, "update"), update);
}

export function markSpam(
  id: string,
  input: { block: boolean; blockDomain?: boolean },
): Promise<TicketDetail> {
  return post(ticketPath(id, "spam"), input);
}

export function markTicketRead(id: string): Promise<{ readAt: string }> {
  return post(ticketPath(id, "read"), {});
}

export function unblockSender(input: {
  email: string;
  reopen: boolean;
}): Promise<{ email: string; reopened: number }> {
  return post(`${BASE}/blocked/unblock`, input);
}

export function startBackfill(): Promise<MailSyncStatus> {
  return post(`${BASE}/backfill`, {});
}

function quotePath(ticketId: string, quoteId: string, action: string) {
  return ticketPath(
    ticketId,
    `quotes/${encodeURIComponent(quoteId)}/${action}`,
  );
}

/** Approving renders the PDF; Send quote (or the operator agent) emails it. */
export function approveQuote(
  ticketId: string,
  quoteId: string,
): Promise<QuoteDetail> {
  return post(quotePath(ticketId, quoteId, "approve"), {});
}

/** The note (1-500 characters) tells the agent what to change. */
export function rejectQuote(
  ticketId: string,
  quoteId: string,
  note: string,
): Promise<QuoteDetail> {
  return post(quotePath(ticketId, quoteId, "reject"), { note });
}

/** Only an approved quote nobody has sent yet. */
export function withdrawQuote(
  ticketId: string,
  quoteId: string,
  note?: string,
): Promise<QuoteDetail> {
  return post(quotePath(ticketId, quoteId, "withdraw"), note ? { note } : {});
}

/**
 * Approves a draft and emails it on the ticket in one step. A failed send
 * leaves the quote approved (`SEND_FAILED`), so the retry is Send quote.
 */
export function approveAndSendQuote(
  ticketId: string,
  quoteId: string,
  idempotencyKey: string,
): Promise<QuoteDetail> {
  return post(quotePath(ticketId, quoteId, "approve-and-send"), {
    idempotencyKey,
  });
}

/** Emails an approved quote on its ticket; the same key twice sends once. */
export function sendQuote(
  ticketId: string,
  quoteId: string,
  idempotencyKey: string,
): Promise<QuoteDetail> {
  return post(quotePath(ticketId, quoteId, "send"), { idempotencyKey });
}

export const QUOTE_SEND_FAILED_MESSAGE =
  "Approved; the email did not go out. Click Send quote to retry.";

/** What an admin reads when a quote decision fails, with the way forward. */
export function quoteWriteFailureMessage(error: unknown): string {
  const failure = error as Partial<InboxWriteFailure> | null;
  const reason = failure?.message ?? "The decision was not saved.";
  if (failure?.code === "SEND_FAILED") return QUOTE_SEND_FAILED_MESSAGE;
  if (failure?.status === 409) {
    return `${reason} The quote changed since this page loaded; it has been reloaded, check its status before trying again.`;
  }
  if (failure?.status === 404) {
    return `${reason} Reload the ticket; the quote may have been replaced by a newer version.`;
  }
  if (failure?.status === 503 && failure.code === "NOT_CONFIGURED") {
    return "The support Worker cannot render quote PDFs yet. Ask the owner to finish its setup, then approve again.";
  }
  return inboxWriteFailureMessage(error);
}

/** A fresh key for a new draft. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** An idempotency key and the exact draft it was made for. */
export interface DraftKey {
  draft: string;
  key: string;
}

/**
 * The key to send this draft with: the same while the draft is unchanged, so
 * a retry sends once, and a new one once it was edited, so the edit is sent
 * instead of nt-support answering with the earlier send.
 */
export function keyForDraft(current: DraftKey | null, draft: string): DraftKey {
  return current?.draft === draft
    ? current
    : { draft, key: newIdempotencyKey() };
}

/** The address each identity sends from. */
export const IDENTITY_ADDRESS: Record<SendIdentity, string> = {
  support: "support@nonresident.tax",
  serkan: "serkan@nonresident.tax",
};

/** What nt-support appends under the reply, shown under the composer. */
export const IDENTITY_SIGNATURE: Record<SendIdentity, string> = {
  support: "Nonresident Tax Team\nsupport@nonresident.tax · nonresident.tax",
  serkan: "Serkan\nNonresident Tax\nnonresident.tax",
};

export const SEND_FAILED_MESSAGE =
  "Gmail didn't accept the reply. Your text is kept; try again, and if it keeps failing check Support → Inbox sync status.";

/** What an operator reads when an inbox write fails: the reason, and what to do next. */
export function inboxWriteFailureMessage(error: unknown): string {
  const failure = error as Partial<InboxWriteFailure> | null;
  const reason = failure?.message ?? "The change was not saved.";
  // nt-support says whether nothing went out or the outcome is unknown (check
  // Gmail's Sent folder first); both keep the text.
  if (failure?.code === "SEND_FAILED") {
    return failure.message
      ? `${failure.message} Your text is kept.`
      : SEND_FAILED_MESSAGE;
  }
  if (failure?.code === "NOT_EMAILED") return NOT_EMAILED_MESSAGE;
  switch (failure?.status) {
    case 400:
      return `${reason} Fix the form and try again.`;
    case 401:
      return "Your session ended. Sign in again, then retry.";
    case 403:
      return "The console refused this change. Reload the page, sign in with your own account (not an API key) and retry.";
    case 404:
      return `${reason} Go back to the inbox; the ticket may have been removed.`;
    case 409:
      return `${reason} Reload the ticket to see its current state before trying again.`;
    case 503:
      return failure.code === "NOT_CONFIGURED"
        ? "Mail sync isn't set up yet: the Gmail connection is missing. Ask the owner to finish setup."
        : "The support Worker did not answer. Reload the page to check whether the change was saved before trying again.";
    case 504:
      return "The support Worker did not answer in time. Reload the ticket to check whether the change was saved before trying again.";
    default:
      // The console's own failure already says it could not reach the Worker.
      return reason.includes("could not be reached")
        ? `${reason} Try again in a minute.`
        : `${reason} The support Worker could not be reached. Try again in a minute.`;
  }
}

/** Comma- or space-separated Cc addresses, de-duplicated. */
export function parseCc(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter((value) => value !== ""),
    ),
  ];
}

const EMAIL = /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]+$/;

export function ccProblem(cc: string[]): string | null {
  if (cc.length > 10) return "Use at most 10 Cc addresses.";
  const bad = cc.find((value) => !EMAIL.test(value));
  return bad === undefined
    ? null
    : `"${bad}" is not an email address. Fix it or remove it from Cc.`;
}
